import React, { useEffect, useState, } from 'react';
import { Calendar, Paperclip } from 'lucide-react';
import { useApp, usePermission, useTask, useUser } from 'hooks';
import { hexToRgba, repareURL, taskBoardActions } from 'utils';
import { ETaskActions } from 'types';
import { AvatarCharecter, AvatarImage } from 'components';

const TaskBoard = ({ dataTasks = null, onAction, projectId }) => {
  const { can } = usePermission()
  const { defaultBoard } = useUser();
  const { data: listData = [] } = dataTasks || {
    totalPage: 0, data: []
  };

  const { getDetail } = useTask();
  const { onUpdateParams, taskFilter } = useApp()

  const [cols, setCols] = useState([]);

  const [tasks, setTasks] = useState([]);

  // const [filter, setFilter] = useState('all');
  // const [searchTerm, setSearchTerm] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);

  useEffect(() => {
    console.log("🚀 ~ TaskBoard ~ defaultBoard?.columns:", defaultBoard?.columns)
    if (defaultBoard?.columns) {
      setCols(defaultBoard.columns)
    }
  }, [defaultBoard]);

  useEffect(() => {
    if (listData?.length) {
      setTasks(listData)
    }
  }, [listData])

  const priorityColors = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800'
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const cbMove = (column_id) => () => {
    setTasks(tasks.map(task =>
      task.id === draggedTask.id
        ? { ...task, column_id }
        : task
    ));
    setDraggedTask(null);
  }

  const handleDrop = (e, column_id, position = 1) => {
    e.preventDefault();
    if (draggedTask) {
      const progress = +(((position - 2) / (cols.length - 2)) * 100).toFixed(2)
      onAction?.(ETaskActions.UPDATE, {
        project_id: projectId,
        id: draggedTask.id,
        column_id,
        progress: progress < 0 ? 0 : progress
      })
      cbMove(column_id);
    }
  };

  const handleActions = async (act: string, taskId: string) => {
    const { data } = await getDetail(projectId, taskId);
    if (data) {
      onUpdateParams({ taskDetail: data })
    }
    onAction?.(act);
  }

  const filteredTasks = listData.filter(task => {
    // const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //   task.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = taskFilter.status === 'all' || task.status === taskFilter.status;//taskFilter.status === 'all' ||  || task.priority === taskFilter.priority;
    return matchesFilter && task.type === 'task';
  });


  const TaskCard = ({ task }) => (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3 cursor-move hover:shadow-md transition-shadow max-w-[350px]"
      draggable
      onDragStart={(e) => handleDragStart(e, task)}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-medium text-gray-900 text-sm leading-5">{task.title}</h3>
        <div className="flex space-x-1">
          {taskBoardActions.map(e => {
            if (!can(e.action, "task")) return null;
            return (
              <button onClick={() => handleActions(e.action, task.id)} key={e.action} className="text-gray-400 hover:text-gray-600">
                {e.icon}
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-gray-600 mb-3">{task?.description}</p>

      <div className="flex flex-wrap gap-1 mb-3">
        {task.tags.map(tag => (
          <span key={tag.id} className="px-2 py-1 text-xs rounded-full" style={{ color: `${tag.color}`, backgroundColor: hexToRgba(`${tag.color}`, 0.3) }}>
            {tag.name}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">
              {task.assignee?.avatar ?
                <AvatarImage className="w-6 h-6 rounded-full" src={repareURL(task.assignee?.avatar)} alt={task.assignee?.name} /> :
                <AvatarCharecter name={task.assignee?.name} />
              }
            </div>
            <span>{task.assignee.name}</span>
          </div>
          {task.total_file > 0 && (
            <div className="flex items-center space-x-1">
              <Paperclip size={12} />
              <span>{task?.total_file}</span>
            </div>
          )}
        </div>
        <span className={`px-2 py-1 rounded-full text-xs ${priorityColors[task?.priority]}`}>
          {task?.priority}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <Calendar size={12} />
          <span>{task?.due_date}</span>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${task?.progress}%` }}
            />
          </div>
          <span>{task?.progress}%</span>
        </div>
      </div>
    </div>
  );

  const KanbanView = () => (
    <div className="overflow-x-auto pb-6 flex gap-4 flex-row">
      {cols.map(colKB => (
        <div
          key={colKB.id}
          className={`min-w-80 rounded-lg p-4  flex-shrink-0`}
          style={{ backgroundColor: hexToRgba(`${colKB.color}`, 0.6) }}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, colKB.id, colKB.position)}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">{colKB.name}</h2>
            <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
              {filteredTasks.filter(task => task.column_id === colKB.id).length}
            </span>
          </div>
          <div className="space-y-3">
            {filteredTasks
              .filter(task => task.column_id === colKB.id)
              .map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
          </div>
        </div>
      ))}
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="max-w-[calc(100vw-113px)]">
        <KanbanView />
      </div>
    </div>
  );
};

export default TaskBoard;